import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import '../../styles/Admin.scss';

import rightArrow1 from '../../assets/rightLast.svg';
import rightArrow2 from '../../assets/right.svg';
import leftArrow1 from '../../assets/leftLast.svg';
import leftArrow2 from '../../assets/left.svg';
import AppSpinner from '../../components/AppSpinner';

interface UserActivityLog {
  _id: string;
  userId: {
    _id: string;
    displayName: string;
    email: string;
  };
  action: string;
  target: string;
  details: string;
  ipAddress: string;
  timestamp: string;
}

const AdminUserActivityPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [logs, setLogs] = useState<UserActivityLog[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalLogs, setTotalLogs] = useState<number>(0);
  const [isViewAdminOnly, setIsViewAdminOnly] = useState<boolean>(false); 
  
  // 검색 및 필터 상태 추가
  const [keyword, setKeyword] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [actionFilter, setActionFilter] = useState<string>('all');
  
  const limit = 10;

  const fetchLogs = async (page: number) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/admin/user-activity-logs', {
        params: {
          page,
          limit,
          type: isViewAdminOnly ? 'admin' : undefined,
          keyword: keyword || undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          action: actionFilter === 'all' ? undefined : actionFilter,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setLogs(response.data.logs);
      setTotalPages(response.data.totalPages);
      setTotalLogs(response.data.totalLogs);
      setError(null);
    } catch (err) {
      setError('활동 로그를 불러오는 데 실패했습니다.');
    }
    finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchLogs(currentPage);
    }
  }, [isAuthenticated, currentPage, isViewAdminOnly]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchLogs(1);
  };

  const handleReset = () => {
    setKeyword('');
    setStartDate('');
    setEndDate('');
    setActionFilter('all');
    setIsViewAdminOnly(false);
    setCurrentPage(1);
    setLoading(true);
    setTimeout(() => fetchLogs(1), 0);
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  if (loading && logs.length === 0) return <AppSpinner fullscreen tip="Loading..." />;

  return (
    <div className="admin-page">
      <h1>사용자 활동 로그</h1>

      <div className="search-filter-container">
        <div className="filter-group">
          <label>기간</label>
          <div className="date-input-group">
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            <span> ~ </span>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
        </div>

        <div className="filter-group">
          <label>활동 유형</label>
          <select value={actionFilter} onChange={(e) => setActionFilter(e.target.value)}>
            <option value="all">전체</option>
            <option value="LOGIN">로그인</option>
            <option value="LOGOUT">로그아웃</option>
            <option value="SIGNUP">회원가입</option>
            <option value="POST_CREATE">게시글 작성</option>
            <option value="POST_UPDATE">게시글 수정</option>
            <option value="POST_DELETE">게시글 삭제</option>
            <option value="PROFILE_UPDATE">프로필 수정</option>
            <option value="COUNSELING_START">상담 시작</option>
          </select>
        </div>

        <div className="filter-group">
          <label>검색</label>
          <input
            type="text"
            placeholder="이름, 이메일, 내용 검색"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
        </div>

        <div className="filter-group" style={{ position: 'relative', top: '-13px', height: '100%' }}>
            <label className="custom-checkbox">
              <input
                type="checkbox"
                checked={isViewAdminOnly}
                onChange={(e) => {
                  setIsViewAdminOnly(e.target.checked);
                  setCurrentPage(1);
                }}
              />
              <span className="checkmark"></span>
              관리자 활동만
            </label>
        </div>

        <div className="button-group">
          <button className="btn-search" onClick={handleSearch}>검색</button>
          <button className="btn-reset" onClick={handleReset}>초기화</button>
        </div>
      </div>

      <div className="list-info">
        총 활동 <span className="count">{totalLogs}</span> | 페이지{' '}
        <span className="page">
          {currentPage} / {totalPages || 1}
        </span>
      </div>

      <div className="table-scroll">
        <table className="table log-list-table">
          <thead>
            <tr>
              <th style={{ width: '200px' }}>시간</th>
              <th style={{ width: '200px' }}>사용자</th>
              <th style={{ width: '180px' }}>활동 유형</th>
              <th>상세 내용</th>
              <th style={{ width: '120px' }}>IP 주소</th>
            </tr>
          </thead>
          <tbody>
            {logs.length > 0 ? (
              logs.map(log => (
                <tr key={log._id}>
                  <td>{new Date(log.timestamp).toLocaleString()}</td>
                  <td>
                    {log.userId ? (
                      <>
                        <div>{log.userId.displayName}</div>
                        <div style={{ fontSize: '0.8em', color: '#888' }}>{log.userId.email}</div>
                      </>
                    ) : (
                      <span style={{ color: '#ccc' }}>(삭제된 사용자)</span>
                    )}
                  </td>
                  <td>
                    <span className="status-badge normal" style={{ fontSize: '0.8rem', padding: '4px 8px', whiteSpace: 'normal', wordBreak: 'break-all' }}>{log.action}</span>
                  </td>
                  <td style={{ textAlign: 'left' }}>{log.details}</td>
                  <td>{log.ipAddress}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="no-data">
                  활동 로그가 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination-container">
        <div className="pagination">
          <button onClick={() => handlePageChange(1)} disabled={currentPage === 1}>
            <img src={leftArrow1} alt="처음" />
          </button>
          <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
            <img src={leftArrow2} alt="이전" />
          </button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum: number;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }
            return (
              <button
                key={pageNum}
                className={currentPage === pageNum ? 'active' : ''}
                onClick={() => handlePageChange(pageNum)}
              >
                {pageNum}
              </button>
            );
          })}
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages || totalPages === 0}
          >
            <img src={rightArrow2} alt="다음" />
          </button>
          <button
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages || totalPages === 0}
          >
            <img src={rightArrow1} alt="끝" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminUserActivityPage;
